import React, { useCallback, useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Package, Upload, Download, Wrench, Barcode } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api2 from "@/lib/api";
import { printBarcodeLabels } from "@/components/BarcodeLabels";
import { ServicePricingFields } from "@/components/ServicePricingFields";
import { isLinear, linearRate, sortTiers } from "@/lib/linearPricing";

const EMPTY = { name: "", type: "product", category: "", barcode: "", cost_price: 0, price: 0, stock: 0, min_stock: 5, image: "", active: true,
  pricing_mode: "fixed", tiers: [], min_charge: 0, material_cost_m: 0, labor_cost_m: 0, margin_pct: 0 };

const str = (v) => (v == null ? "" : String(v));
const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

export default function Products() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => api.get("/products").then((r) => setItems(r.data)), []);
  const loadCategories = useCallback(() => api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); loadCategories(); }, [load, loadCategories]);

  const openNew = (type = "product") => { setForm({ ...EMPTY, type }); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    const safe = {
      name: str(p.name),
      type: p.type === "service" ? "service" : "product",
      category: str(p.category),
      barcode: str(p.barcode),
      image: str(p.image),
      cost_price: num(p.cost_price, 0),
      price: num(p.price, 0),
      stock: num(p.stock, 0),
      min_stock: num(p.min_stock, 5),
      active: p.active !== false,
      pricing_mode: p.pricing_mode || "fixed",
      tiers: (p.tiers || []).map((t) => ({ up_to: t.up_to ?? "", price_per_m: t.price_per_m })),
      min_charge: num(p.min_charge, 0),
      material_cost_m: num(p.material_cost_m, 0),
      labor_cost_m: num(p.labor_cost_m, 0),
      margin_pct: num(p.margin_pct, 0),
    };
    setForm(safe);
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Informe o nome");
    const linear = form.type === "service" && isLinear(form);
    const tiers = sortTiers((form.tiers || []).filter((t) => t.price_per_m !== "" && t.price_per_m != null))
      .map((t) => ({ up_to: t.up_to === "" || t.up_to == null ? null : Number(t.up_to), price_per_m: Number(t.price_per_m) }));
    if (linear && form.pricing_mode === "linear_tiers" && !tiers.length) return toast.error("Cadastre ao menos uma faixa de preço");
    const minTier = tiers.length ? Math.min(...tiers.map((t) => t.price_per_m)) : 0;
    const linearPrice = !linear ? 0 : +(form.pricing_mode === "linear_tiers" ? minTier : linearRate(form, 1)).toFixed(2);
    if (linear && linearPrice <= 0) return toast.error("Informe os valores por metro");
    if (!linear && (!Number(form.price) || Number(form.price) <= 0)) return toast.error("Informe um preço de venda válido");
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        category: form.category || null,
        barcode: form.type === "product" ? (form.barcode || null) : null,
        image: form.image || null,
        active: !!form.active,
        cost_price: Number(form.cost_price) || 0,
        price: linear ? linearPrice : Number(form.price),
        stock: form.type === "product" ? (Number(form.stock) || 0) : 0,
        min_stock: form.type === "product" ? (Number(form.min_stock) || 0) : 0,
        ...(form.type === "service" ? {
          pricing_mode: form.pricing_mode || "fixed", tiers,
          min_charge: Number(form.min_charge) || 0,
          material_cost_m: Number(form.material_cost_m) || 0,
          labor_cost_m: Number(form.labor_cost_m) || 0,
          ...(form.pricing_mode === "linear_material" ? { margin_pct: Number(form.margin_pct) || 0 } : {}),
        } : {}),
      };
      if (editId) await api.patch(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      const label = form.type === "service" ? "Serviço" : "Produto";
      setOpen(false);
      toast.success(editId ? `${label} atualizado` : `${label} criado`);
      // Defer refetch until Radix Dialog has fully unmounted — avoids React 19 portal
      // reconciliation race ("removeChild: node is not a child of this node").
      setTimeout(load, 350);
    } catch (e) {
      const d = e.response?.data?.detail;
      let msg = "Erro ao salvar";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x.msg).join(", ");
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este produto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Excluído"); load();
  };

  const downloadTemplate = () => {
    const csv = "name,type,category,barcode,cost_price,price,stock,min_stock,image\nCafé Exemplo,product,Alimentos,7891234567800,15,29.90,50,10,\nServiço Exemplo,service,Serviços,,0,120,0,0,\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "modelo_produtos.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!importFile) return toast.error("Selecione um arquivo CSV");
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const { data } = await api2.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Importado: ${data.created} novos, ${data.updated} atualizados`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na importação");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Catálogo</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Produtos & Serviços</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => printBarcodeLabels(items.filter((p) => p.type === "product"))}
            data-testid="print-labels-btn" disabled={!items.some((p) => p.type === "product")}>
            <Barcode className="w-4 h-4 mr-2" /> Imprimir Etiquetas
          </Button>
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-2" /> Importar CSV
            </Button>
          )}
          <Button variant="outline" onClick={() => openNew("service")} data-testid="new-service-btn">
            <Wrench className="w-4 h-4 mr-2" /> Novo Serviço
          </Button>
          <Button onClick={() => openNew("product")} className="bg-blue-600 hover:bg-blue-700" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Novo Produto
          </Button>
        </div>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-right">Estoque</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {p.image ? <img src={p.image} alt="" className="w-8 h-8 object-cover rounded" /> :
                        <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center"><Package className="w-4 h-4 text-slate-400" /></div>}
                      {p.name}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{p.type === "service" ? "Serviço" : "Produto"}</Badge></TableCell>
                  <TableCell>{p.category || "—"}</TableCell>
                  <TableCell className="text-right kpi-num">{brl(p.cost_price)}</TableCell>
                  <TableCell className="text-right kpi-num text-blue-600">{brl(p.price)}{isLinear(p) && <span className="text-[10px] text-slate-500">/m</span>}</TableCell>
                  <TableCell className="text-right">
                    {p.type === "product" ? (
                      <span className={"kpi-num " + (p.stock < p.min_stock ? "text-rose-600 font-bold" : "")}>{p.stock}</span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Edit className="w-4 h-4" /></Button>
                    {user?.role === "admin" && (
                      <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(p.id)} data-testid={`del-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhum produto cadastrado</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {form.type === "service" ? <Wrench className="w-5 h-5 text-blue-600" /> : <Package className="w-5 h-5 text-blue-600" />}
              <span>{`${editId ? "Editar" : "Novo"} ${form.type === "service" ? "Serviço" : "Produto"}`}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome *</Label>
              <Input value={str(form.name)} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" autoFocus /></div>
            <div className="col-span-2"><Label>Tipo</Label>
              <Select value={form.type || "product"} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="prod-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Produto (controla estoque)</SelectItem>
                  <SelectItem value="service">Serviço (sem estoque)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Categoria</Label>
              <Input list="product-categories" value={str(form.category)}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="prod-cat"
                placeholder={form.type === "service" ? "Serviços" : "Alimentos, Eletrônicos..."} />
              <datalist id="product-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist></div>
            {form.type === "product" && (
              <div><Label>Código de barras</Label>
                <Input value={str(form.barcode)} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="prod-barcode" /></div>
            )}
            <div className={form.type === "product" ? "" : "col-span-2"}><Label>URL da imagem</Label>
              <Input value={str(form.image)} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="prod-image" placeholder="https://..." /></div>
            <div><Label>Preço de custo</Label>
              <Input type="number" step="0.01" value={str(form.cost_price)} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="prod-cost" /></div>
            {!(form.type === "service" && isLinear(form)) && <div><Label>Preço de venda *</Label>
              <div className="flex gap-1">
                <Input type="number" step="0.01" value={str(form.price)} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" className="flex-1" />
                <Button type="button" variant="outline" size="sm" title="Sugerir preço (margem 100% + 30%)"
                  onClick={() => {
                    const c = Number(form.cost_price) || 0;
                    if (c <= 0) return toast.error("Informe o custo antes de sugerir");
                    const sug = +(c * 2 * 1.3).toFixed(2);
                    setForm({ ...form, price: sug });
                    toast.success(`Sugerido R$ ${sug.toFixed(2).replace(".", ",")} (margem 100% + 30%)`);
                  }}
                  data-testid="suggest-price-btn">
                  Sugerir
                </Button>
              </div>
              {Number(form.cost_price) > 0 && Number(form.price) > 0 && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Margem: {(((Number(form.price) - Number(form.cost_price)) / Number(form.cost_price)) * 100).toFixed(0)}% sobre custo
                </div>
              )}
            </div>}
            {form.type === "service" && <ServicePricingFields form={form} setForm={setForm} />}
            {form.type === "product" && <>
              <div><Label>Estoque</Label>
                <Input type="number" value={str(form.stock)} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="prod-stock" /></div>
              <div><Label>Estoque mínimo</Label>
                <Input type="number" value={str(form.min_stock)} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="prod-min" /></div>
            </>}
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="prod-active" />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-product-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-display">Importar produtos via CSV</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Envie um arquivo <b>.csv</b> com as colunas: <code className="text-xs">name, type, category, barcode, cost_price, price, stock, min_stock, image</code>.
              Produtos existentes (por código de barras ou nome) serão atualizados.
            </p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Baixar modelo CSV
            </Button>
            <Input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            {importResult && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="font-medium">Resultado:</div>
                <div className="text-emerald-600">✓ {importResult.created} criados</div>
                <div className="text-blue-600">↻ {importResult.updated} atualizados</div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-rose-600 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                    {importResult.errors.map((e, i) => <div key={`err-${i}-${String(e).slice(0, 24)}`}>• {e}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Fechar</Button>
            <Button onClick={doImport} className="bg-blue-600 hover:bg-blue-700" disabled={importing || !importFile} data-testid="do-import-btn">
              {importing ? "Importando…" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
